import { useState, type KeyboardEvent, type RefObject } from 'react';
import { Box, IconButton, TextField, Tooltip } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';

export const MAX_MESSAGE_CHARS = 2000;

interface Props {
  pending: boolean;
  inputRef: RefObject<HTMLTextAreaElement>;
  onSend: (text: string) => void;
  onStop: () => void;
}

/** Enter sends, Shift+Enter is a newline; while an answer is pending Send becomes Stop. */
export default function AssistantComposer({ pending, inputRef, onSend, onStop }: Props) {
  const [text, setText] = useState('');

  const submit = () => {
    if (pending || !text.trim()) return;
    onSend(text);
    setText('');
  };

  const onKeyDown = (e: KeyboardEvent) => {
    // isComposing: Enter confirms an IME candidate, it must not send.
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
      <TextField
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        inputRef={inputRef}
        autoFocus
        fullWidth
        multiline
        maxRows={5}
        size="small"
        placeholder="Ask about this group…"
        inputProps={{ maxLength: MAX_MESSAGE_CHARS, 'aria-label': 'Ask Tripsby a question' }}
      />
      {pending ? (
        <Tooltip title="Stop">
          <IconButton color="error" aria-label="Stop" onClick={onStop}>
            <StopIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Send">
          <span>
            <IconButton color="primary" aria-label="Send question" onClick={submit} disabled={!text.trim()}>
              <SendIcon />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
}
